import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import MfaVerify from './MfaVerify';
import MfaSetup from './MfaSetup';

const LOGO = '/dailyreps3.png';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function Login() {
  const { loginStaff, completeMfaLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSession, setMfaSession] = useState(null);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [setupToken, setSetupToken] = useState(null);

  // Forgot password states
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: forgotEmail, type: 'staff' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginStaff(email, password);

      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaSession(data.mfa_session);
        setLoading(false);
        return;
      }

      if (data.mfa_setup_required) {
        setMfaSetupRequired(true);
        setSetupToken(data.token);
        setLoading(false);
        return;
      }

      // Normal login completed via AuthContext
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaComplete = (data) => {
    completeMfaLogin(data);
  };

  const handleMfaBack = () => {
    setMfaRequired(false);
    setMfaSession(null);
    setEmail('');
    setPassword('');
  };

  if (mfaRequired) {
    return <MfaVerify mfaSession={mfaSession} onComplete={handleMfaComplete} onBack={handleMfaBack} />;
  }

  if (mfaSetupRequired) {
    return <MfaSetup token={setupToken} onComplete={handleMfaComplete} />;
  }

  if (forgotMode) {
    return (
      <div className="login-page">
        <img src={LOGO} alt="Train Daily" className="login-logo" />
        <h1 className="login-title">
          <span>Train Daily</span> Coach Login
        </h1>
        {forgotSent ? (
          <div style={{ textAlign: 'center', maxWidth: 360 }}>
            <p style={{ color: '#fff', fontSize: '0.95rem', lineHeight: 1.5 }}>
              If an account exists with that email, a password reset link has been sent. Check your inbox.
            </p>
            <button
              onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }}
              style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 12, fontSize: '0.9rem' }}
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <>
            <p style={{ color: '#fff', fontSize: '0.9rem', textAlign: 'center', marginBottom: 16 }}>
              Enter your email to receive a password reset link.
            </p>
            <form className="login-form" onSubmit={handleForgotSubmit}>
              {forgotError && <div className="login-error">{forgotError}</div>}
              <input
                type="email"
                placeholder="Email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
              <button className="btn btn-orange" type="submit" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <button
              onClick={() => { setForgotMode(false); setForgotError(''); setForgotEmail(''); }}
              style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 20, fontSize: '0.9rem' }}
            >
              Back to Sign In
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="login-page">
      <img src={LOGO} alt="Train Daily" className="login-logo" />
      <h1 className="login-title">
        <span>Train Daily</span> Coach Login
      </h1>
      <form className="login-form" onSubmit={handleSubmit}>
        {error && <div className="login-error">{error}</div>}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button className="btn btn-orange" type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      <button
        onClick={() => setForgotMode(true)}
        style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 20, fontSize: '0.9rem' }}
      >
        Forgot Password?
      </button>
      <p style={{ marginTop: 24, fontSize: '0.85rem', color: '#888', textAlign: 'center' }}>
        Are you a player? Ask your coach for your team's login link.
      </p>
    </div>
  );
}
